import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { userService } from '@/services/admin/user.service';
import { roleService } from '@/services/admin/roles.service';
import type { Role } from '@/services/admin/role.types';
import { toast } from 'sonner';

interface UserRolesTabProps {
  userId: string;
}

export function UserRolesTab({ userId }: UserRolesTabProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  // Fetch user's current roles
  const { data: userRoles, isLoading: loadingUserRoles } = useQuery<string[]>({
    queryKey: ['users', userId, 'roles'],
    queryFn: async () => {
      const roles = await userService.getUserRoles(userId);
      console.log('Fetched user roles:', roles);
      return roles;
    },
  });

  // Sync selected roles when userRoles changes
  useEffect(() => {
    if (userRoles) {
      console.log('Setting selected roles to:', userRoles);
      setSelectedRoles(userRoles);
    }
  }, [userRoles]);

  // Fetch all available roles
  const { data: allRolesData, isLoading: loadingAllRoles } = useQuery({
    queryKey: ['roles', 'list', { filter: searchTerm }],
    queryFn: () => roleService.getRoles({ filter: searchTerm, maxResultCount: 100 }),
  });

  const updateRolesMutation = useMutation({
    mutationFn: (roleNames: string[]) => userService.updateUserRoles(userId, roleNames),
    onSuccess: async () => {
      console.log('Role update successful, invalidating cache...');
      
      // Invalidate and refetch the user roles
      await queryClient.invalidateQueries({ queryKey: ['users', userId, 'roles'] });
      await queryClient.invalidateQueries({ queryKey: ['users', userId] });
      
      // Force an immediate refetch to verify the changes
      await queryClient.refetchQueries({ queryKey: ['users', userId, 'roles'] });
      
      console.log('Cache invalidated and refetched');
      toast.success('User roles updated successfully');
      setHasChanges(false);
    },
    onError: (error: Error) => {
      console.error('Failed to update user roles:', error);
      toast.error(`Failed to update roles: ${error.message}`);
    },
  });

  const handleRoleToggle = (roleName: string) => {
    const newRoles = selectedRoles.includes(roleName)
      ? selectedRoles.filter((r) => r !== roleName)
      : [...selectedRoles, roleName];

    setSelectedRoles(newRoles);
    const currentRoles = (userRoles || []).slice().sort();
    const updatedRoles = newRoles.slice().sort();
    setHasChanges(JSON.stringify(currentRoles) !== JSON.stringify(updatedRoles));
  };

  const handleSave = () => {
    updateRolesMutation.mutate(selectedRoles);
  };

  const handleCancel = () => {
    setSelectedRoles(userRoles || []);
    setHasChanges(false);
  };

  const isLoading = loadingUserRoles || loadingAllRoles;

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const availableRoles: Role[] = allRolesData?.items || [];
  const assignedCount = selectedRoles.length;
  const availableCount = availableRoles.length - assignedCount;

  return (
    <div className="space-y-6">
      {/* Header with Save Button */}
      {hasChanges && (
        <Card className="border-primary bg-primary/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-medium">You have unsaved changes</p>
              <p className="text-sm text-muted-foreground">
                {assignedCount} role{assignedCount !== 1 ? 's' : ''} will be assigned to this user
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateRolesMutation.isPending}>
                {updateRolesMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Assigned Roles</CardDescription>
            <CardTitle className="text-2xl">{assignedCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Available Roles</CardDescription>
            <CardTitle className="text-2xl">{availableCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Search */}
      <div>
        <Input
          placeholder="Search roles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Roles List */}
      <Card>
        <CardHeader>
          <CardTitle>Available Roles</CardTitle>
          <CardDescription>
            Select roles to assign to this user. Changes must be saved.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {availableRoles.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No roles found
            </p>
          ) : (
            <div className="space-y-1">
              {availableRoles.map((role) => {
                const isAssigned = selectedRoles.includes(role.name);
                return (
                  <div
                    key={role.id}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Checkbox
                        checked={isAssigned}
                        onCheckedChange={() => handleRoleToggle(role.name)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{role.name}</p>
                          {role.isDefault && (
                            <Badge variant="secondary" className="text-xs">Default</Badge>
                          )}
                          {role.isPublic && (
                            <Badge variant="outline" className="text-xs">Public</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {isAssigned && (
                      <Badge variant="success" className="ml-2">
                        Assigned
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
